import axios from 'axios';

/**
 * Project Service
 * Handles all API calls related to projects
 */
export const projectService = {
  /**
   * Get all projects
   * @param {Object} params - Query parameters (filters, pagination, etc.)
   * @returns {Promise<Object>} Projects data with pagination
   */
  async getAllProjects(params = {}) {
    try {
      const response = await axios.get('/projects', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  /**
   * Get running project journeys per phase
   * @param {Object} params - { owner_id, search }
   * @returns {Promise<Object>} { phases, deals } where each deal carries its `project`
   */
  async getJourneyBoard(params = {}) {
    try {
      const response = await axios.get('/projects/journey-board', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching project journey board:', error);
      throw error;
    }
  },

  /**
   * Get a single project by ID
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Project data
   */
  async getProjectById(projectId) {
    try {
      const response = await axios.get(`/projects/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching project:', error);
      throw error;
    }
  },

  /**
   * Create a new project
   * @param {Object} projectData - Project data
   * @returns {Promise<Object>} Created project
   */
  async createProject(projectData) {
    try {
      const response = await axios.post('/projects', projectData);
      return response.data;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  },

  /**
   * Update a project
   * @param {string} projectId - Project ID
   * @param {Object} projectData - Updated project data
   * @returns {Promise<Object>} Updated project
   */
  async updateProject(projectId, projectData) {
    try {
      const response = await axios.patch(`/projects/${projectId}`, projectData);
      return response.data;
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  },

  /**
   * Get open projects that still hold the lead's previous address
   * @param {string} leadId - Lead ID
   * @param {Object} previousAddress - The address the lead had before the change
   * @returns {Promise<Object>} { lead_address, projects }
   */
  async previewLeadAddressSync(leadId, previousAddress = null) {
    try {
      const response = await axios.post('/projects/address-sync/preview', {
        lead_id: leadId,
        previous_address: previousAddress,
      });
      return response.data;
    } catch (error) {
      console.error('Error checking project addresses:', error);
      throw error;
    }
  },

  /**
   * Copy the lead's address onto its outdated open projects
   * @param {string} leadId - Lead ID
   * @param {Array<string>} projectIds - Optional subset of projects to update
   * @returns {Promise<Object>} { updated, failed }
   */
  async applyLeadAddressSync(leadId, projectIds = null) {
    try {
      const response = await axios.post('/projects/address-sync/apply', {
        lead_id: leadId,
        project_ids: projectIds,
      });
      return response.data;
    } catch (error) {
      console.error('Error syncing project addresses:', error);
      throw error;
    }
  },

  /**
   * Delete a project
   * @param {string} projectId - Project ID
   * @returns {Promise<void>}
   */
  async deleteProject(projectId) {
    try {
      await axios.delete(`/projects/${projectId}`);
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  },

  /**
   * Mark project as complete
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Updated project
   */
  async completeProject(projectId) {
    try {
      const response = await axios.patch(`/projects/${projectId}/complete`);
      return response.data;
    } catch (error) {
      console.error('Error completing project:', error);
      throw error;
    }
  },

  /**
   * Get project timeline
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} Timeline events
   */
  async getProjectTimeline(projectId) {
    try {
      const response = await axios.get(`/projects/${projectId}/timeline`);
      return response.data;
    } catch (error) {
      console.error('Error fetching project timeline:', error);
      throw error;
    }
  },

  /**
   * Get project by deal ID
   * @param {string} dealId - Deal ID
   * @returns {Promise<Object|null>} Project data or null
   */
  async getProjectByDealId(dealId) {
    try {
      const response = await axios.get('/projects', {
        params: { deal_id: dealId, limit: 1 },
      });
      const projects = response.data?.projects || response.data?.data || [];
      return projects.length > 0 ? projects[0] : null;
    } catch (error) {
      console.error('Error fetching project by deal:', error);
      throw error;
    }
  },

  /**
   * Get project scope (enriched snapshot for packing slips, work order items)
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} { scope, proposal_id, synced_at }
   */
  async getScope(projectId) {
    try {
      const response = await axios.get(`/projects/${projectId}/scope`);
      return response.data;
    } catch (error) {
      console.error('Error fetching project scope:', error);
      throw error;
    }
  },

  /**
   * Get per-product work order assignments for soft guidance when splitting a
   * project across work orders. Returns, per product (index into
   * project.scope.products), whether it is already assigned to a work order and
   * which work order(s) it sits on. Products are NOT exclusive — this is purely
   * informational to avoid accidental double splitting.
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} { assignments: [{ product_index, assigned, work_orders }] }
   */
  async getProductAssignments(projectId) {
    try {
      const response = await axios.get(`/projects/${projectId}/product-assignments`);
      return response.data;
    } catch (error) {
      console.error('Error fetching project product assignments:', error);
      throw error;
    }
  },

  /**
   * Sync project scope from accepted proposal
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Updated project
   */
  async syncScope(projectId) {
    try {
      const response = await axios.post(`/projects/${projectId}/sync-scope`);
      return response.data;
    } catch (error) {
      console.error('Error syncing project scope:', error);
      throw error;
    }
  },

  /**
   * Reopen a completed project (for meerwerk)
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Updated project
   */
  async reopenProject(projectId) {
    try {
      const response = await axios.patch(`/projects/${projectId}/reopen`);
      return response.data;
    } catch (error) {
      console.error('Error reopening project:', error);
      throw error;
    }
  },
};

export default projectService;
